import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import ClientHeader from "../../components/Client/ClientHeader";
import ProductCard from "../../components/Client/ProductCard";
import StoreHeader from "../../components/Client/StoreHeader";
import StoreCategoryChips from "../../components/Client/StoreCategoryChips";
import CategoryMegaMenu from "../../components/Client/CategoryMegaMenu";
import Footer from "../../components/Client/Footer";
import Skeleton from "../../components/Client/Skeleton";
import ProductFilterSidebar from "../../components/Client/ProductFilterSidebar";
import { getProducts } from "../../api/productService";
import { getStoreById, getStoreCategories } from "../../api/storeService";
import { getCategories } from "../../api/categoryService";
import { useLanguage } from "../../context/LanguageContext";
import { pickLocalizedName } from "../../utils/localizedName";
import { createEmptyFilters, filterProducts, sortProducts, hasActiveFilters } from "../../utils/productFilters";
import "./ProductsList.css";

function SearchIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="11" cy="11" r="8" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  );
}

function FilterIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="4" y1="6" x2="20" y2="6" />
      <line x1="7" y1="12" x2="17" y2="12" />
      <line x1="10" y1="18" x2="14" y2="18" />
    </svg>
  );
}

export default function ProductsList() {
  const { t } = useTranslation(["productsList", "common"]);
  const { language } = useLanguage();
  const [searchParams] = useSearchParams();
  const storeId = searchParams.get("storeId") || undefined;
  const search = searchParams.get("search") || undefined;
  const highlight = searchParams.get("highlight") || undefined;
  const initialCategoryId = searchParams.get("categoryId") || undefined;
  const categoryId = !storeId ? initialCategoryId : undefined;

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState(createEmptyFilters);
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);

  const [store, setStore] = useState(null);
  const [storeLoading, setStoreLoading] = useState(!!storeId);
  const [storeCategories, setStoreCategories] = useState([]);
  const [categoryPath, setCategoryPath] = useState([]);
  const [storeSelectedCategoryId, setStoreSelectedCategoryId] = useState(initialCategoryId ? Number(initialCategoryId) : null);
  const [storeSearch, setStoreSearch] = useState("");

  useEffect(() => {
    if (!storeId) {
      setStore(null);
      setStoreCategories([]);
      setCategoryPath([]);
      setStoreSelectedCategoryId(null);
      return;
    }

    setStoreLoading(true);
    Promise.all([getStoreById(storeId), getStoreCategories(storeId)])
      .then(([storeData, categoriesData]) => {
        setStore(storeData);
        setStoreCategories(categoriesData);
      })
      .catch(() => {
        setStore(null);
        setStoreCategories([]);
      })
      .finally(() => setStoreLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeId]);

  const effectiveCategoryId = storeId ? storeSelectedCategoryId ?? undefined : categoryId;
  const effectiveSearch = storeId ? storeSearch.trim() || undefined : search;

  useEffect(() => {
    setLoading(true);
    getProducts({ categoryId: effectiveCategoryId, storeId, search: effectiveSearch, highlight })
      .then(setProducts)
      .catch(() => setProducts([]))
      .finally(() => setLoading(false));
  }, [effectiveCategoryId, storeId, effectiveSearch, highlight]);

  useEffect(() => {
    setFilters(createEmptyFilters());
    setMobileFilterOpen(false);
  }, [effectiveCategoryId, storeId, effectiveSearch]);

  const filteredProducts = sortProducts(filterProducts(products, filters), filters.sort);

  const [categoryChain, setCategoryChain] = useState([]);

  useEffect(() => {
    if (!categoryId) {
      setCategoryChain([]);
      return;
    }
    getCategories({})
      .then((cats) => {
        const byId = new Map(cats.map((c) => [c.categoryId, c]));
        const chain = [];
        let current = byId.get(Number(categoryId));
        while (current) {
          chain.unshift(current);
          current = current.parentCategoryId ? byId.get(current.parentCategoryId) : null;
        }
        setCategoryChain(chain);
      })
      .catch(() => setCategoryChain([]));
  }, [categoryId]);

  function handleSelectAllCategories() {
    setCategoryPath([]);
    setStoreSelectedCategoryId(null);
  }

  function handleSelectCategory(category, isLeaf) {
    if (isLeaf) {
      setStoreSelectedCategoryId(category.categoryId);
    } else {
      setCategoryPath((prev) => [...prev, category]);
      setStoreSelectedCategoryId(null);
    }
  }

  function handleBackCategory() {
    setCategoryPath((prev) => prev.slice(0, -1));
    setStoreSelectedCategoryId(null);
  }

  const lastCategory = categoryChain[categoryChain.length - 1];
  let title = t("productsTitle");
  if (search) title = t("resultsForSearch", { search });
  else if (categoryId) {
    title = (lastCategory && pickLocalizedName(language, lastCategory.name, lastCategory.nameArabic, lastCategory.nameHebrew)) || t("productsTitle");
  }

  if (storeId) {
    return (
      <div className="client-products-page">
        <ClientHeader />
        <div className="client-products-content">
          <StoreHeader store={store} loading={storeLoading} />

          {!storeLoading && (
            <>
              <div className="client-store-search-row">
                <span className="client-store-search-icon">
                  <SearchIcon />
                </span>
                <input
                  type="search"
                  placeholder={t("searchInStorePlaceholder", { storeName: store?.storeName || t("thisStore") })}
                  value={storeSearch}
                  onChange={(e) => setStoreSearch(e.target.value)}
                  className="client-store-search-input"
                  aria-label={t("searchInStoreAriaLabel")}
                />
              </div>

              <StoreCategoryChips
                categories={storeCategories}
                categoryPath={categoryPath}
                selectedCategoryId={storeSelectedCategoryId}
                onSelectAll={handleSelectAllCategories}
                onSelectCategory={handleSelectCategory}
                onBack={handleBackCategory}
              />
            </>
          )}

          {loading ? (
            <div className="client-products-grid">
              {Array.from({ length: 10 }).map((_, index) => (
                <Skeleton className="client-product-skeleton" key={index} />
              ))}
            </div>
          ) : products.length === 0 ? (
            <p className="client-products-status">{t("noProductsFound")}</p>
          ) : (
            <div className="client-products-layout">
              <ProductFilterSidebar
                products={products}
                filters={filters}
                onChange={setFilters}
                mobileOpen={mobileFilterOpen}
                onCloseMobile={() => setMobileFilterOpen(false)}
              />
              <div className="client-products-main">
                <div className="client-products-toolbar">
                  <button
                    type="button"
                    className="client-products-filter-button"
                    onClick={() => setMobileFilterOpen(true)}
                  >
                    <FilterIcon /> {t("filters")}
                  </button>
                  <span className="client-products-count">{t("resultsCount", { count: filteredProducts.length })}</span>
                  <select
                    className="client-products-sort"
                    value={filters.sort}
                    onChange={(e) => setFilters((prev) => ({ ...prev, sort: e.target.value }))}
                    aria-label={t("sortBy")}
                  >
                    <option value="newest">{t("sortNewest")}</option>
                    <option value="priceAsc">{t("sortPriceAsc")}</option>
                    <option value="priceDesc">{t("sortPriceDesc")}</option>
                    <option value="bestselling">{t("sortBestselling")}</option>
                    <option value="toprated">{t("sortTopRated")}</option>
                  </select>
                </div>

                {filteredProducts.length === 0 ? (
                  <p className="client-products-status">
                    {hasActiveFilters(filters) ? t("noProductsMatchFilters") : t("noProductsFound")}
                  </p>
                ) : (
                  <div className="client-products-grid">
                    {filteredProducts.map((product) => (
                      <ProductCard product={product} key={product.productId} />
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="client-products-page">
      <ClientHeader />
      <CategoryMegaMenu />
      <div className="client-products-content">
        {categoryChain.length > 1 && (
          <nav className="client-products-breadcrumb" aria-label={t("categoryPathAriaLabel")}>
            {categoryChain.map((c, index) => {
              const name = pickLocalizedName(language, c.name, c.nameArabic, c.nameHebrew);
              return (
                <span key={c.categoryId} className="client-products-breadcrumb-part">
                  {index > 0 && <span className="client-products-breadcrumb-sep">›</span>}
                  {index === categoryChain.length - 1 ? (
                    <span className="client-products-breadcrumb-current">{name}</span>
                  ) : (
                    <Link to={`/products?categoryId=${c.categoryId}`} className="client-products-breadcrumb-link">
                      {name}
                    </Link>
                  )}
                </span>
              );
            })}
          </nav>
        )}

        <h1 className="client-products-title">{title}</h1>

        {loading ? (
          <div className="client-products-grid">
            {Array.from({ length: 10 }).map((_, index) => (
              <Skeleton className="client-product-skeleton" key={index} />
            ))}
          </div>
        ) : products.length === 0 ? (
          <p className="client-products-status">{t("noProductsFound")}</p>
        ) : (
          <div className="client-products-layout">
            <ProductFilterSidebar
              products={products}
              filters={filters}
              onChange={setFilters}
              mobileOpen={mobileFilterOpen}
              onCloseMobile={() => setMobileFilterOpen(false)}
            />
            <div className="client-products-main">
              <div className="client-products-toolbar">
                <button
                  type="button"
                  className="client-products-filter-button"
                  onClick={() => setMobileFilterOpen(true)}
                >
                  <FilterIcon /> {t("filters")}
                </button>
                <span className="client-products-count">{t("resultsCount", { count: filteredProducts.length })}</span>
                <select
                  className="client-products-sort"
                  value={filters.sort}
                  onChange={(e) => setFilters((prev) => ({ ...prev, sort: e.target.value }))}
                  aria-label={t("sortBy")}
                >
                  <option value="newest">{t("sortNewest")}</option>
                  <option value="priceAsc">{t("sortPriceAsc")}</option>
                  <option value="priceDesc">{t("sortPriceDesc")}</option>
                  <option value="bestselling">{t("sortBestselling")}</option>
                  <option value="toprated">{t("sortTopRated")}</option>
                </select>
              </div>

              {filteredProducts.length === 0 ? (
                <p className="client-products-status">
                  {hasActiveFilters(filters) ? t("noProductsMatchFilters") : t("noProductsFound")}
                </p>
              ) : (
                <div className="client-products-grid">
                  {filteredProducts.map((product) => (
                    <ProductCard product={product} key={product.productId} />
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}
